import { useState, useEffect, useCallback, useRef } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, Users, ArrowLeft, Target, BookOpen, MessageSquare, FileText,
  Plus, Trash2, Save, Send, Check, X, Eye, EyeOff, ExternalLink, ChevronDown
} from "lucide-react";

export default function AdminClientMgmt() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await http.get("/client/admin/clients");
      setClients(data);
    } catch { toast.error("Failed to load clients"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const addClient = async () => {
    if (!addForm.name.trim() || !addForm.email.trim()) { toast.error("Name and email are required"); return; }
    if (!addForm.password || addForm.password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setAdding(true);
    try {
      await http.post("/client/admin/clients", addForm);
      toast.success("Client created");
      setShowAdd(false);
      setAddForm({ name: "", email: "", phone: "", password: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create client"); }
    finally { setAdding(false); }
  };

  if (loading) return <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-[#C9A055]" /></div>;

  if (selectedClient) {
    return <ClientDetail client={selectedClient} onBack={() => { setSelectedClient(null); load(); }} />;
  }

  return (
    <div className="space-y-6" data-testid="admin-client-mgmt">
      <h2 className="font-display text-3xl text-[#F4F1EA]">Client Management</h2>
      <div className="flex justify-between items-center">
        <p className="text-zinc-400 text-sm">{clients.length} client{clients.length !== 1 ? "s" : ""}</p>
        <button onClick={() => setShowAdd(!showAdd)} data-testid="add-client-btn"
          className="flex items-center gap-2 bg-[#C9A055] text-[#1A1A1A] px-4 py-2 rounded-lg text-xs font-medium hover:bg-[#D4AF37] transition-all">
          <Plus size={14} /> Add Client
        </button>
      </div>

      {showAdd && (
        <div className="rounded-xl border border-[#C9A055]/20 bg-[#C9A055]/[0.03] p-5 space-y-4" data-testid="add-client-form">
          <h4 className="text-[#F4F1EA] text-sm font-medium">Create New Client</h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-zinc-400 text-xs uppercase tracking-wider block mb-1.5">Full Name *</label>
              <input value={addForm.name} onChange={e => setAddForm(p => ({ ...p, name: e.target.value }))}
                placeholder="e.g. Jane Smith" data-testid="add-client-name"
                className="w-full bg-transparent border border-white/10 rounded-lg px-3 py-2.5 text-sm text-[#F4F1EA] focus:border-[#C9A055] focus:outline-none" />
            </div>
            <div>
              <label className="text-zinc-400 text-xs uppercase tracking-wider block mb-1.5">Email *</label>
              <input type="email" value={addForm.email} onChange={e => setAddForm(p => ({ ...p, email: e.target.value }))}
                placeholder="jane@example.com" data-testid="add-client-email"
                className="w-full bg-transparent border border-white/10 rounded-lg px-3 py-2.5 text-sm text-[#F4F1EA] focus:border-[#C9A055] focus:outline-none" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-zinc-400 text-xs uppercase tracking-wider block mb-1.5">Phone</label>
              <input value={addForm.phone} onChange={e => setAddForm(p => ({ ...p, phone: e.target.value }))}
                placeholder="(555) 123-4567"
                className="w-full bg-transparent border border-white/10 rounded-lg px-3 py-2.5 text-sm text-[#F4F1EA] focus:border-[#C9A055] focus:outline-none" />
            </div>
            <div>
              <label className="text-zinc-400 text-xs uppercase tracking-wider block mb-1.5">Temporary Password *</label>
              <input value={addForm.password} onChange={e => setAddForm(p => ({ ...p, password: e.target.value }))}
                placeholder="Min 6 characters" data-testid="add-client-password"
                className="w-full bg-transparent border border-white/10 rounded-lg px-3 py-2.5 text-sm text-[#F4F1EA] focus:border-[#C9A055] focus:outline-none" />
            </div>
          </div>
          <p className="text-zinc-600 text-[10px]">The client can change their password after logging in at the Client Portal.</p>
          <div className="flex gap-2">
            <button onClick={addClient} disabled={adding} data-testid="save-client-btn"
              className="flex items-center gap-2 bg-[#C9A055] text-[#1A1A1A] px-5 py-2 rounded-lg text-xs font-medium hover:bg-[#D4AF37] disabled:opacity-50">
              {adding ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Create Client
            </button>
            <button onClick={() => setShowAdd(false)} className="text-zinc-400 text-xs px-4 py-2">Cancel</button>
          </div>
        </div>
      )}
      {clients.length === 0 && (
        <div className="text-center py-16 text-zinc-500">
          <Users size={40} className="mx-auto mb-4 opacity-30" />
          <p>No clients registered yet.</p>
        </div>
      )}
      <div className="space-y-2">
        {clients.map(c => (
          <button key={c.id} onClick={() => setSelectedClient(c)} data-testid={`client-row-${c.id}`}
            className="w-full rounded-lg border border-white/[0.06] bg-white/[0.02] p-4 flex items-center justify-between hover:border-[#C9A055]/30 transition-all text-left">
            <div className="min-w-0">
              <p className="text-[#F4F1EA] font-medium truncate">{c.name}</p>
              <p className="text-zinc-500 text-xs">{c.email}</p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <span className={`text-[10px] font-medium uppercase tracking-wider px-2 py-0.5 rounded-full ${
                c.status === "active" ? "bg-green-900/20 text-green-400" : "bg-red-900/20 text-red-400"
              }`}>{c.status}</span>
              <span className="text-zinc-600 text-xs">{c.created_at ? new Date(c.created_at).toLocaleDateString() : ""}</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

/* ═══ CLIENT DETAIL ═══ */
function ClientDetail({ client, onBack }) {
  const [tab, setTab] = useState("goals");

  const tabs = [
    { key: "goals", label: "Goals", icon: <Target size={14} /> },
    { key: "notes", label: "Session Notes", icon: <FileText size={14} /> },
    { key: "resources", label: "Resources", icon: <BookOpen size={14} /> },
    { key: "messages", label: "Messages", icon: <MessageSquare size={14} /> },
  ];

  return (
    <div className="space-y-6" data-testid="client-detail">
      <div className="flex items-center gap-4">
        <button onClick={onBack} className="text-zinc-500 hover:text-[#C9A055] transition-colors" data-testid="back-to-clients">
          <ArrowLeft size={18} />
        </button>
        <div>
          <h2 className="text-[#F4F1EA] text-xl font-medium">{client.name}</h2>
          <p className="text-zinc-500 text-xs">{client.email} · {client.phone || "No phone"}</p>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`client-tab-${t.key}`}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs font-medium tracking-wide transition-all ${
              tab === t.key ? "bg-[#C9A055] text-[#1A1A1A]" : "border border-white/10 text-zinc-400 hover:border-[#C9A055]/40"
            }`}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {tab === "goals" && <AdminGoals clientId={client.id} />}
      {tab === "notes" && <AdminNotes clientId={client.id} />}
      {tab === "resources" && <AdminResources clientId={client.id} />}
      {tab === "messages" && <AdminMessages clientId={client.id} />}
    </div>
  );
}

/* ═══ GOALS ═══ */
function AdminGoals({ clientId }) {
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", steps: [""] });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await http.get(`/portal/admin/goals?client_id=${clientId}`);
      setGoals(data);
    } catch { toast.error("Failed to load goals"); }
    finally { setLoading(false); }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.title.trim()) { toast.error("Title required"); return; }
    setSaving(true);
    try {
      await http.post("/portal/admin/goals", {
        client_id: clientId,
        title: form.title,
        description: form.description,
        action_steps: form.steps.filter(s => s.trim()).map(s => ({ text: s })),
      });
      toast.success("Goal created");
      setShowCreate(false);
      setForm({ title: "", description: "", steps: [""] });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this goal?")) return;
    try { await http.delete(`/portal/admin/goals/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  const archive = async (id) => {
    try { await http.patch(`/portal/admin/goals/${id}`, { status: "archived" }); toast.success("Archived"); load(); }
    catch { toast.error("Failed"); }
  };

  if (loading) return <Loader2 className="animate-spin text-[#C9A055] mx-auto" />;

  const STATUS_STYLE = { active: "text-yellow-400 bg-yellow-900/20", completed: "text-green-400 bg-green-900/20", archived: "text-zinc-400 bg-zinc-800" };

  return (
    <div className="space-y-4" data-testid="admin-goals">
      <div className="flex justify-between items-center">
        <p className="text-zinc-400 text-sm">{goals.length} goal{goals.length !== 1 ? "s" : ""}</p>
        <button onClick={() => setShowCreate(!showCreate)} data-testid="create-goal-btn"
          className="flex items-center gap-2 bg-[#C9A055] text-[#1A1A1A] px-4 py-2 rounded-lg text-xs font-medium hover:bg-[#D4AF37]">
          <Plus size={14} /> New Goal
        </button>
      </div>

      {showCreate && (
        <div className="rounded-xl border border-[#C9A055]/20 bg-[#C9A055]/[0.03] p-5 space-y-4" data-testid="create-goal-form">
          <input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="Goal title *"
            className="w-full bg-transparent border border-white/10 rounded-lg px-3 py-2.5 text-sm text-[#F4F1EA] focus:border-[#C9A055] focus:outline-none" data-testid="goal-title-input" />
          <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Description" rows={2}
            className="w-full bg-transparent border border-white/10 rounded-lg px-3 py-2.5 text-sm text-[#F4F1EA] focus:border-[#C9A055] focus:outline-none resize-none" />
          <div>
            <p className="text-zinc-400 text-xs uppercase tracking-wider mb-2">Action Steps</p>
            {form.steps.map((s, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <input value={s} onChange={e => { const ns = [...form.steps]; ns[i] = e.target.value; setForm(p => ({ ...p, steps: ns })); }}
                  placeholder={`Step ${i + 1}`} data-testid={`goal-step-${i}`}
                  className="flex-1 bg-transparent border border-white/10 rounded px-2.5 py-2 text-sm text-[#F4F1EA] focus:border-[#C9A055] focus:outline-none" />
                {form.steps.length > 1 && (
                  <button onClick={() => setForm(p => ({ ...p, steps: p.steps.filter((_, j) => j !== i) }))} className="text-zinc-600 hover:text-red-400"><Trash2 size={14} /></button>
                )}
              </div>
            ))}
            <button onClick={() => setForm(p => ({ ...p, steps: [...p.steps, ""] }))} className="text-[#C9A055] text-xs hover:text-[#D4AF37]">+ Add step</button>
          </div>
          <div className="flex gap-2">
            <button onClick={create} disabled={saving} data-testid="save-goal-btn"
              className="flex items-center gap-2 bg-[#C9A055] text-[#1A1A1A] px-5 py-2 rounded-lg text-xs font-medium disabled:opacity-50">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Create
            </button>
            <button onClick={() => setShowCreate(false)} className="text-zinc-400 text-xs px-4 py-2">Cancel</button>
          </div>
        </div>
      )}

      {goals.map(g => (
        <div key={g.id} className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-5 space-y-3" data-testid={`goal-${g.id}`}>
          <div className="flex justify-between items-start">
            <div>
              <span className={`text-[10px] font-medium uppercase tracking-wider px-2 py-0.5 rounded-full ${STATUS_STYLE[g.status] || ""}`}>{g.status}</span>
              <h4 className="text-[#F4F1EA] font-medium mt-1">{g.title}</h4>
              {g.description && <p className="text-zinc-500 text-xs mt-1">{g.description}</p>}
            </div>
            <div className="flex gap-1">
              {g.status === "active" && <button onClick={() => archive(g.id)} className="text-zinc-500 hover:text-zinc-300 p-1 text-[10px]">Archive</button>}
              <button onClick={() => del(g.id)} className="text-zinc-600 hover:text-red-400 p-1"><Trash2 size={12} /></button>
            </div>
          </div>
          {g.action_steps?.length > 0 && (
            <div className="space-y-1.5 pl-1">
              {g.action_steps.map(s => (
                <div key={s.id} className="flex items-center gap-2">
                  {s.completed ? <Check size={12} className="text-green-400" /> : <div className="w-3 h-3 rounded-full border border-zinc-600" />}
                  <span className={`text-xs ${s.completed ? "text-zinc-500 line-through" : "text-zinc-300"}`}>{s.text}</span>
                </div>
              ))}
              <p className="text-zinc-600 text-[10px] mt-1">
                {g.action_steps.filter(s => s.completed).length}/{g.action_steps.length} completed
              </p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/* ═══ SESSION NOTES ═══ */
function AdminNotes({ clientId }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ private_notes: "", client_summary: "", published: false, appointment_id: "" });
  const [appointments, setAppointments] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [n, a] = await Promise.all([
        http.get(`/portal/admin/session-notes?client_id=${clientId}`),
        http.get("/booking/admin/appointments"),
      ]);
      setNotes(n.data);
      // Filter appointments for this client
      const client = await http.get("/client/admin/clients").then(r => r.data.find(c => c.id === clientId));
      const email = client?.email || "";
      setAppointments(a.data.filter(ap => ap.client_email === email));
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setSaving(true);
    try {
      await http.post("/portal/admin/session-notes", { ...form, client_id: clientId });
      toast.success("Note saved");
      setShowCreate(false);
      setForm({ private_notes: "", client_summary: "", published: false, appointment_id: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const togglePublish = async (id, current) => {
    try {
      await http.patch(`/portal/admin/session-notes/${id}`, { published: !current });
      toast.success(current ? "Unpublished" : "Published to client");
      load();
    } catch { toast.error("Failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this note?")) return;
    try { await http.delete(`/portal/admin/session-notes/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  if (loading) return <Loader2 className="animate-spin text-[#C9A055] mx-auto" />;

  return (
    <div className="space-y-4" data-testid="admin-notes">
      <div className="flex justify-between items-center">
        <p className="text-zinc-400 text-sm">{notes.length} note{notes.length !== 1 ? "s" : ""}</p>
        <button onClick={() => setShowCreate(!showCreate)} data-testid="create-note-btn"
          className="flex items-center gap-2 bg-[#C9A055] text-[#1A1A1A] px-4 py-2 rounded-lg text-xs font-medium hover:bg-[#D4AF37]">
          <Plus size={14} /> New Note
        </button>
      </div>

      {showCreate && (
        <div className="rounded-xl border border-[#C9A055]/20 bg-[#C9A055]/[0.03] p-5 space-y-4" data-testid="create-note-form">
          <div>
            <label className="text-zinc-400 text-xs uppercase tracking-wider block mb-1.5">Linked Appointment</label>
            <select value={form.appointment_id} onChange={e => setForm(p => ({ ...p, appointment_id: e.target.value }))}
              className="w-full bg-[#1A1A1A] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-[#F4F1EA] focus:border-[#C9A055] focus:outline-none">
              <option value="">No linked appointment</option>
              {appointments.map(a => <option key={a.id} value={a.id}>{a.date} — {a.service_title}</option>)}
            </select>
          </div>
          <div>
            <label className="text-zinc-400 text-xs uppercase tracking-wider block mb-1.5">Private Notes (admin only)</label>
            <textarea value={form.private_notes} onChange={e => setForm(p => ({ ...p, private_notes: e.target.value }))} rows={4}
              placeholder="These notes are only visible to you..."
              className="w-full bg-transparent border border-white/10 rounded-lg px-3 py-2.5 text-sm text-[#F4F1EA] focus:border-[#C9A055] focus:outline-none resize-none" data-testid="private-notes-input" />
          </div>
          <div>
            <label className="text-zinc-400 text-xs uppercase tracking-wider block mb-1.5">Client Summary (visible to client when published)</label>
            <textarea value={form.client_summary} onChange={e => setForm(p => ({ ...p, client_summary: e.target.value }))} rows={3}
              placeholder="A brief summary for your client..."
              className="w-full bg-transparent border border-white/10 rounded-lg px-3 py-2.5 text-sm text-[#F4F1EA] focus:border-[#C9A055] focus:outline-none resize-none" data-testid="client-summary-input" />
          </div>
          <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
            <input type="checkbox" checked={form.published} onChange={e => setForm(p => ({ ...p, published: e.target.checked }))} className="accent-[#C9A055]" />
            Publish summary to client immediately
          </label>
          <div className="flex gap-2">
            <button onClick={create} disabled={saving} data-testid="save-note-btn"
              className="flex items-center gap-2 bg-[#C9A055] text-[#1A1A1A] px-5 py-2 rounded-lg text-xs font-medium disabled:opacity-50">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save Note
            </button>
            <button onClick={() => setShowCreate(false)} className="text-zinc-400 text-xs px-4 py-2">Cancel</button>
          </div>
        </div>
      )}

      {notes.map(n => (
        <div key={n.id} className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-5 space-y-3" data-testid={`note-${n.id}`}>
          <div className="flex justify-between items-start">
            <div className="flex items-center gap-2">
              <span className="text-zinc-600 text-xs">{new Date(n.created_at).toLocaleDateString()}</span>
              {n.published ? (
                <span className="text-[10px] text-green-400 bg-green-900/20 px-2 py-0.5 rounded-full flex items-center gap-1"><Eye size={10} /> Published</span>
              ) : (
                <span className="text-[10px] text-zinc-500 bg-zinc-800 px-2 py-0.5 rounded-full flex items-center gap-1"><EyeOff size={10} /> Private</span>
              )}
            </div>
            <div className="flex gap-1">
              <button onClick={() => togglePublish(n.id, n.published)} className="text-zinc-500 hover:text-[#C9A055] p-1 text-[10px]">
                {n.published ? "Unpublish" : "Publish"}
              </button>
              <button onClick={() => del(n.id)} className="text-zinc-600 hover:text-red-400 p-1"><Trash2 size={12} /></button>
            </div>
          </div>
          {n.private_notes && (
            <div className="bg-red-900/10 border border-red-500/10 rounded-lg p-3">
              <p className="text-red-400 text-[10px] uppercase tracking-wider mb-1">Private (admin only)</p>
              <p className="text-zinc-300 text-sm whitespace-pre-wrap">{n.private_notes}</p>
            </div>
          )}
          {n.client_summary && (
            <div className="bg-white/[0.02] border border-white/[0.04] rounded-lg p-3">
              <p className="text-[#C9A055] text-[10px] uppercase tracking-wider mb-1">Client Summary</p>
              <p className="text-zinc-300 text-sm whitespace-pre-wrap">{n.client_summary}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/* ═══ RESOURCES ═══ */
function AdminResources({ clientId }) {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", type: "link", url: "" });
  const [saving, setSaving] = useState(false);

  const TYPES = ["link", "pdf", "video", "worksheet"];

  const load = useCallback(async () => {
    try {
      const { data } = await http.get(`/portal/admin/resources?client_id=${clientId}`);
      setResources(data);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.title.trim()) { toast.error("Title required"); return; }
    setSaving(true);
    try {
      await http.post("/portal/admin/resources", { ...form, assigned_to: clientId });
      toast.success("Resource added");
      setShowCreate(false);
      setForm({ title: "", description: "", type: "link", url: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete?")) return;
    try { await http.delete(`/portal/admin/resources/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  if (loading) return <Loader2 className="animate-spin text-[#C9A055] mx-auto" />;

  const TYPE_STYLE = { link: "bg-blue-900/20 text-blue-400", pdf: "bg-red-900/20 text-red-400", video: "bg-purple-900/20 text-purple-400", worksheet: "bg-amber-900/20 text-amber-400" };

  return (
    <div className="space-y-4" data-testid="admin-resources">
      <div className="flex justify-between items-center">
        <p className="text-zinc-400 text-sm">{resources.length} resource{resources.length !== 1 ? "s" : ""}</p>
        <button onClick={() => setShowCreate(!showCreate)} data-testid="create-resource-btn"
          className="flex items-center gap-2 bg-[#C9A055] text-[#1A1A1A] px-4 py-2 rounded-lg text-xs font-medium hover:bg-[#D4AF37]">
          <Plus size={14} /> Add Resource
        </button>
      </div>

      {showCreate && (
        <div className="rounded-xl border border-[#C9A055]/20 bg-[#C9A055]/[0.03] p-5 space-y-4" data-testid="create-resource-form">
          <input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="Resource title *"
            className="w-full bg-transparent border border-white/10 rounded-lg px-3 py-2.5 text-sm text-[#F4F1EA] focus:border-[#C9A055] focus:outline-none" data-testid="resource-title-input" />
          <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Description" rows={2}
            className="w-full bg-transparent border border-white/10 rounded-lg px-3 py-2.5 text-sm text-[#F4F1EA] focus:border-[#C9A055] focus:outline-none resize-none" />
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-zinc-400 text-xs uppercase tracking-wider block mb-1.5">Type</label>
              <select value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))}
                className="w-full bg-[#1A1A1A] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-[#F4F1EA] focus:border-[#C9A055] focus:outline-none">
                {TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
              </select>
            </div>
            <div>
              <label className="text-zinc-400 text-xs uppercase tracking-wider block mb-1.5">URL</label>
              <input value={form.url} onChange={e => setForm(p => ({ ...p, url: e.target.value }))} placeholder="https://..."
                className="w-full bg-transparent border border-white/10 rounded-lg px-3 py-2.5 text-sm text-[#F4F1EA] focus:border-[#C9A055] focus:outline-none" data-testid="resource-url-input" />
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={create} disabled={saving} data-testid="save-resource-btn"
              className="flex items-center gap-2 bg-[#C9A055] text-[#1A1A1A] px-5 py-2 rounded-lg text-xs font-medium disabled:opacity-50">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Add
            </button>
            <button onClick={() => setShowCreate(false)} className="text-zinc-400 text-xs px-4 py-2">Cancel</button>
          </div>
        </div>
      )}

      {resources.map(r => (
        <div key={r.id} className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-4 flex items-center justify-between gap-4" data-testid={`resource-${r.id}`}>
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className={`text-[10px] font-medium uppercase tracking-wider px-2 py-0.5 rounded-full ${TYPE_STYLE[r.type] || "bg-zinc-800 text-zinc-400"}`}>{r.type}</span>
              {r.assigned_to === "all" && <span className="text-[10px] text-zinc-500">All clients</span>}
            </div>
            <p className="text-[#F4F1EA] text-sm font-medium truncate">{r.title}</p>
            {r.description && <p className="text-zinc-500 text-xs truncate">{r.description}</p>}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {r.url && <a href={r.url} target="_blank" rel="noreferrer" className="text-zinc-500 hover:text-[#C9A055] p-1"><ExternalLink size={14} /></a>}
            <button onClick={() => del(r.id)} className="text-zinc-600 hover:text-red-400 p-1"><Trash2 size={14} /></button>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ═══ MESSAGES ═══ */
function AdminMessages({ clientId }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await http.get(`/portal/admin/messages/${clientId}`);
      setMessages(data);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      await http.post(`/portal/admin/messages/${clientId}`, { text: text.trim() });
      setText("");
      load();
    } catch { toast.error("Failed to send"); }
    finally { setSending(false); }
  };

  if (loading) return <Loader2 className="animate-spin text-[#C9A055] mx-auto" />;

  return (
    <div className="space-y-4" data-testid="admin-messages">
      <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] max-h-[400px] overflow-y-auto p-4 space-y-3">
        {messages.length === 0 && <p className="text-zinc-600 text-sm text-center py-8">No messages yet. Start the conversation.</p>}
        {messages.map(m => (
          <div key={m.id} className={`flex ${m.sender === "admin" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[70%] rounded-2xl px-4 py-2.5 ${
              m.sender === "admin"
                ? "bg-[#C9A055]/20 text-[#F4F1EA] rounded-br-sm"
                : "bg-white/[0.06] text-zinc-300 rounded-bl-sm"
            }`} data-testid={`msg-${m.id}`}>
              <p className="text-sm whitespace-pre-wrap">{m.text}</p>
              <p className="text-[10px] text-zinc-600 mt-1">{new Date(m.created_at).toLocaleString()}</p>
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>
      <div className="flex gap-2">
        <input value={text} onChange={e => setText(e.target.value)} placeholder="Type a message..."
          onKeyDown={e => e.key === "Enter" && !e.shiftKey && send()}
          className="flex-1 bg-transparent border border-white/10 rounded-xl px-4 py-3 text-sm text-[#F4F1EA] focus:border-[#C9A055] focus:outline-none"
          data-testid="admin-msg-input" />
        <button onClick={send} disabled={sending || !text.trim()} data-testid="admin-msg-send"
          className="bg-[#C9A055] text-[#1A1A1A] px-5 py-3 rounded-xl text-sm font-medium hover:bg-[#D4AF37] disabled:opacity-50 flex items-center gap-2">
          {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
        </button>
      </div>
    </div>
  );
}
